import React from 'react';
import MuiButton from '@mui/material/Button';
import { palette } from '@src/theme';
import merge from 'lodash/merge';
import { InternalToggleButtonProps } from './interface';

// TODO: Solve layout shifting on default toggle button when selecting/deselecting
export const InternalToggleButton = (props: InternalToggleButtonProps) => {
  const {
    children,
    selected,
    onChange,
    disabled,
    startIcon,
    endIcon,
  } = props;

  const sx = getSX(props);

  return (
    <MuiButton
      startIcon={startIcon}
      endIcon={endIcon}
      disabled={disabled}
      sx={sx}
      disableElevation
      onClick={() => onChange(!selected)}
    >
      {children}
    </MuiButton>
  );
};

function getSX(props: InternalToggleButtonProps) {
  const {
    color,
    selected,
    size,
    shape = 'square',
    grouped = false,
    firstButton = false,
    lastButton = false,
    variant = 'outlined',
  } = props;

  const isSquareShape = shape === 'square';
  const isWarningStyle = color === 'warning';
  const isErrorStyle = color === 'error';

  const borderRadius = (() => {
    if (grouped) {
      return '0px';
    }
    if (isSquareShape) {
      return '4px';
    }
    return undefined;
  })();

  const borderColor = (() => {
    if (isErrorStyle) {
      return palette.state.error.secondary;
    }
    if (isWarningStyle) {
      return palette.state.warning.secondary;
    }
    return selected ? palette.state.teal.primary : palette.controls.outline;
  })();

  const horizontalBorderWidth = (() => {
    if (!grouped) {
      return;
    }
    if (isErrorStyle || isWarningStyle) {
      return '1px';
    }
    return selected ? '2px' : '0.5px';
  })();

  const firstLastGroupBorderWidth = (() => {
    if (!grouped) {
      return;
    }
    if (isErrorStyle || isWarningStyle || selected) {
      return '2px';
    }
    return '1px';
  })();

  const selectedTextColor = (() => {
    if (isWarningStyle) {
      return palette.state.warning.secondary;
    }
    if (isErrorStyle) {
      return palette.state.error.secondary;
    }
    return palette.state.teal.secondary;
  })();

  const selectedBackgroundColor = (() => {
    if (isWarningStyle) {
      return palette.state.warning['backing-1'];
    }
    if (isErrorStyle) {
      return palette.state.error['backing-1'];
    }
    return palette.state.teal['backing-1'];
  })();

  const hoverBackgroundColor = (() => {
    if (isWarningStyle) {
      return palette.state.warning.backing;
    }
    if (isErrorStyle) {
      return palette.state.error.backing;
    }
    return palette.state.teal.backing;
  })();

  const variantSX = (() => {
    return variant === 'outlined' ? {
      borderColor: borderColor,
      borderStyle: 'solid',
      borderRadius: borderRadius,
  
      // default border width, override for grouped buttons
      borderWidth: selected || isWarningStyle || isErrorStyle ? '2px' : '1px',
      // halve the horizontal border width when in button groups
      borderLeftWidth: firstButton && grouped ? firstLastGroupBorderWidth : horizontalBorderWidth,
      borderRightWidth: lastButton && grouped ? firstLastGroupBorderWidth : horizontalBorderWidth,
  
      backgroundColor: selected ? selectedBackgroundColor : undefined,
      color: selected ? selectedTextColor : palette.text.secondary,
      // only apply border radius to first and last button in group
      '&:first-of-type': {
        borderTopLeftRadius: grouped ? '4px' : undefined,
        borderBottomLeftRadius: grouped ? '4px' : undefined,
      },
      '&:last-of-type': {
        borderTopRightRadius: grouped ? '4px' : undefined,
        borderBottomRightRadius: grouped ? '4px' : undefined,
      },
      '&:hover': {
        backgroundColor: selected ? selectedBackgroundColor : hoverBackgroundColor,
      },
      '&:focus': {
        backgroundColor: selected ? selectedBackgroundColor : palette.surface['hover+focused'],
      },
      '&:active': {
        backgroundColor: selected ? selectedBackgroundColor : palette.surface.pressed,
      },
    } : {
      backgroundColor: undefined,
      color: palette.state.teal.secondary,
      '&:hover': {
        backgroundColor: palette.state.teal.backing,
      },
      '&:active': {
        backgroundColor: palette.state.teal['backing-1'],
      },
      '&:focus': {
        border: `2px solid ${palette.state.teal.secondary}`,
      },
    };
  })();

  const layoutSX = (() => {
    if (variant === 'outlined') {
      return {};
    }
    
    return {
      padding: size === 'small' ? '3px 9px 3px 9px' : '6px 16px 6px 16px'
    };
  })();

  return merge({
    ...variantSX,
    ...layoutSX,
  });
}